import { useEditor, useEditorState, EditorContent } from "@tiptap/react";
import { Extension, Node } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import ImageExt from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import { TextStyle, FontFamily, FontSize } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { cn } from "@/v5/lib/utils";
import { useFieldLocked } from "@/components/ui/FieldLock";
import { useCallback, useEffect, useState } from "react";
import type { Editor } from "@tiptap/react";
export type { Editor };
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Link as LinkIcon,
  List,
  ListOrdered,
  Heading2,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Image,
  Undo,
  Redo,
  Code2,
  Table as TableIcon,
  Rows,
  Columns,
  Trash2,
  Type,
} from "lucide-react";

// ─── Font controls (opt-in: fontControls) ─────────────────────────────────────

/**
 * The corporate dashboard's editor fonts (its ALERT_TITLE_FONTS), written as
 * the same CSS stacks so text styled in either editor matches.
 */
const FONT_FAMILIES: { label: string; css: string }[] = [
  { label: "Arial", css: "Arial, Helvetica, sans-serif" },
  { label: "Arial Black", css: '"Arial Black", "Avant Garde", sans-serif' },
  { label: "Comic Sans MS", css: '"Comic Sans MS", sans-serif' },
  { label: "Courier New", css: '"Courier New", Courier, monospace' },
  { label: "Georgia", css: "Georgia, Palatino, serif" },
  { label: "Helvetica", css: "Helvetica, Arial, sans-serif" },
  { label: "Impact", css: "Impact, Chicago, sans-serif" },
  { label: "Palatino", css: '"Palatino Linotype", "Book Antiqua", Palatino, serif' },
  { label: "Script", css: '"Segoe Script", "Brush Script MT", cursive' },
  { label: "Times New Roman", css: '"Times New Roman", Times, serif' },
  { label: "Trebuchet MS", css: '"Trebuchet MS", Geneva, sans-serif' },
  { label: "Verdana", css: "Verdana, Geneva, sans-serif" },
];

/** The corporate dashboard's sizes (its font sizes 1–7). */
const FONT_SIZES = ["8pt", "10pt", "12pt", "14pt", "18pt", "24pt", "36pt"];

const TOOLBAR_SELECT =
  "h-8 rounded-md border border-border bg-background px-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring";

/** A font stack as the browser may write it back (quotes, spacing), for matching. */
function fontKey(css: string): string {
  return css.replace(/["']/g, "").replace(/\s*,\s*/g, ",").trim().toLowerCase();
}

/** The list's own stack for a stored font, or the stored font itself. */
function matchFont(css: string): string {
  if (!css) return "";
  return FONT_FAMILIES.find((f) => fontKey(f.css) === fontKey(css))?.css ?? css;
}

/** A CSS colour as the colour input takes it (#rrggbb); black when unknown. */
function toHex(color: string): string {
  const c = color.trim();
  if (/^#[0-9a-f]{6}$/i.test(c)) return c.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(c)) return `#${c.slice(1).split("").map((x) => x + x).join("")}`.toLowerCase();
  const m = c.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (m) return `#${[m[1], m[2], m[3]].map((n) => Number(n).toString(16).padStart(2, "0")).join("")}`;
  return "#000000";
}

// ─── Custom extensions ────────────────────────────────────────────────────────

/**
 * Pass legacy/template HTML attributes through the schema so rich note
 * templates (tables with bgcolor, cells with colspan, styled wrappers) survive
 * round-trips through the editor.
 */
const PreserveAttributes = Extension.create({
  name: "preserveAttributes",
  addGlobalAttributes() {
    return [
      {
        types: [
          "paragraph",
          "heading",
          "tableCell",
          "tableHeader",
          "tableRow",
          "image",
          "div",
        ],
        attributes: {
          style: {
            default: null,
            parseHTML: (el) => el.getAttribute("style"),
            renderHTML: (attrs) =>
              attrs.style ? { style: attrs.style as string } : {},
          },
          class: {
            default: null,
            parseHTML: (el) => el.getAttribute("class"),
            renderHTML: (attrs) =>
              attrs.class ? { class: attrs.class as string } : {},
          },
          colspan: {
            default: null,
            parseHTML: (el) => el.getAttribute("colspan"),
            renderHTML: (attrs) =>
              attrs.colspan ? { colspan: attrs.colspan as string } : {},
          },
          rowspan: {
            default: null,
            parseHTML: (el) => el.getAttribute("rowspan"),
            renderHTML: (attrs) =>
              attrs.rowspan ? { rowspan: attrs.rowspan as string } : {},
          },
          align: {
            default: null,
            parseHTML: (el) => el.getAttribute("align"),
            renderHTML: (attrs) =>
              attrs.align ? { align: attrs.align as string } : {},
          },
          valign: {
            default: null,
            parseHTML: (el) => el.getAttribute("valign"),
            renderHTML: (attrs) =>
              attrs.valign ? { valign: attrs.valign as string } : {},
          },
          width: {
            default: null,
            parseHTML: (el) => el.getAttribute("width"),
            renderHTML: (attrs) =>
              attrs.width ? { width: attrs.width as string } : {},
          },
          bgcolor: {
            default: null,
            parseHTML: (el) => el.getAttribute("bgcolor"),
            renderHTML: (attrs) =>
              attrs.bgcolor ? { bgcolor: attrs.bgcolor as string } : {},
          },
        },
      },
    ];
  },
});

/**
 * Minimal <div> block node so wrapper divs in legacy templates survive parse.
 * Used together with PreserveAttributes to keep style/class on the div.
 */
const DivBlock = Node.create({
  name: "div",
  group: "block",
  content: "block+",
  defining: true,
  parseHTML() {
    return [{ tag: "div" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", HTMLAttributes, 0];
  },
});

interface TiptapEditorProps {
  content?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  className?: string;
  editorClassName?: string;
  editorRef?: React.RefObject<Editor | null>;
  /**
   * Show the toolbar's HTML source toggle (default). Off for a host with its
   * own source mode, e.g. one that must keep the stored HTML byte for byte:
   * this toggle shows (and re-parses) the editor's normalized HTML.
   */
  showSourceToggle?: boolean;
  /**
   * Font, font size and text colour in the toolbar, as the corporate
   * dashboard's editor has them (off by default). Also keeps the fonts and
   * sizes already in the HTML, which an editor without them drops.
   */
  fontControls?: boolean;
}

function ToolbarButton({
  active,
  onClick,
  title,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      className={cn(
        "h-8 w-8 inline-flex items-center justify-center rounded-md transition-colors",
        active
          ? "bg-primary/10 text-primary"
          : "text-muted-foreground hover:bg-accent hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

export function TiptapEditor({
  content = "",
  onChange,
  placeholder = "Start typing...",
  className,
  editorClassName,
  editorRef,
  showSourceToggle = true,
  fontControls = false,
}: TiptapEditorProps) {
  // Inside a locked FieldLockScope (a Locked Admin form): the content only, not
  // editable, with no toolbar.
  const locked = useFieldLocked();
  const editor = useEditor({
    immediatelyRender: true,
    editable: !locked,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: "text-primary underline" },
      }),
      ImageExt,
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder }),
      TextStyle,
      Color,
      ...(fontControls ? [FontFamily, FontSize] : []),
      Highlight.configure({ multicolor: true }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class:
            "border-collapse border border-slate-300 my-2 w-full table-auto",
        },
      }),
      TableRow,
      TableHeader.configure({
        HTMLAttributes: {
          class: "border border-slate-300 bg-slate-50 px-2 py-1 align-top",
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: "border border-slate-300 px-2 py-1 align-top",
        },
      }),
      DivBlock,
      PreserveAttributes,
    ],
    content,
    parseOptions: { preserveWhitespace: "full" },
    onUpdate({ editor: e }) {
      onChange?.(e.getHTML());
    },
    editorProps: {
      attributes: {
        class: cn(
          "tiptap-content focus:outline-none min-h-[200px] px-4 py-3",
          editorClassName,
        ),
      },
    },
  });

  // The selection's font, size and colour, so the toolbar shows what is there.
  const textStyle = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      const attrs = (e?.getAttributes("textStyle") ?? {}) as Record<string, unknown>;
      return {
        fontFamily: typeof attrs.fontFamily === "string" ? attrs.fontFamily : "",
        fontSize: typeof attrs.fontSize === "string" ? attrs.fontSize.replace(/\s/g, "") : "",
        color: typeof attrs.color === "string" ? attrs.color : "",
      };
    },
  });

  useEffect(() => {
    if (editorRef) {
      (editorRef as React.MutableRefObject<Editor | null>).current = editor;
    }
  }, [editor, editorRef]);

  // A scope that locks or unlocks after mount. No update event: nothing was edited.
  useEffect(() => {
    if (editor && editor.isEditable === locked) editor.setEditable(!locked, false);
  }, [editor, locked]);

  // ─── HTML source mode ───────────────────────────────────────────────────
  // When on, the body becomes a textarea bound to the editor's getHTML().
  // Toggling off commits the textarea back into the editor via setContent
  // with preserveWhitespace so indentation is kept.
  const [sourceMode, setSourceMode] = useState(false);
  const [sourceValue, setSourceValue] = useState("");

  const enterSourceMode = useCallback(() => {
    if (!editor) return;
    setSourceValue(editor.getHTML());
    setSourceMode(true);
  }, [editor]);

  const exitSourceMode = useCallback(() => {
    if (!editor) return;
    editor.commands.setContent(sourceValue, {
      parseOptions: { preserveWhitespace: "full" },
    });
    setSourceMode(false);
  }, [editor, sourceValue]);

  const toggleSourceMode = useCallback(() => {
    if (sourceMode) {
      exitSourceMode();
    } else {
      enterSourceMode();
    }
  }, [sourceMode, enterSourceMode, exitSourceMode]);

  const addLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
  }, [editor]);

  const addImage = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("Image URL");
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  if (!editor) return null;

  const fontValue = matchFont(textStyle.fontFamily);
  const sizeValue = textStyle.fontSize;

  return (
    <div className={cn("border border-border rounded-lg overflow-hidden bg-background flex flex-col", className)}>
      <style>{`
        .tiptap-content { max-width: none; font-size: 0.875rem; line-height: 1.625; }
        .tiptap-content h1 { font-size: 1.5em; font-weight: 700; margin: 0.75em 0 0.4em; line-height: 1.3; }
        .tiptap-content h2 { font-size: 1.3em; font-weight: 700; margin: 0.75em 0 0.4em; line-height: 1.3; }
        .tiptap-content h3 { font-size: 1.15em; font-weight: 600; margin: 0.6em 0 0.3em; line-height: 1.4; }
        .tiptap-content ul { list-style-type: disc; padding-left: 1.5em; margin: 0.5em 0; }
        .tiptap-content ol { list-style-type: decimal; padding-left: 1.5em; margin: 0.5em 0; }
        .tiptap-content li { margin: 0.25em 0; }
        .tiptap-content li > p { margin: 0; }
        .tiptap-content blockquote { border-left: 3px solid hsl(var(--border)); padding-left: 1em; margin: 0.5em 0; color: hsl(var(--muted-foreground)); }
        .tiptap-content a { color: hsl(var(--primary)); text-decoration: underline; }
        .tiptap-content p { margin: 0.4em 0; }
        .tiptap-content img { max-width: 100%; height: auto; }
        .tiptap-content table { border-collapse: collapse; border: 1px solid #cbd5e1; margin: 0.5rem 0; width: 100%; table-layout: auto; }
        .tiptap-content td, .tiptap-content th { border: 1px solid #cbd5e1; padding: 0.25rem 0.5rem; vertical-align: top; }
        .tiptap-content th { background: #f8fafc; }
        .tiptap-content .is-empty::before { content: attr(data-placeholder); color: hsl(var(--muted-foreground)); pointer-events: none; float: left; height: 0; }
      `}</style>
      {/* SCRUM-1444: the toolbar wraps to two rows on a phone and was reported cut off. The
          strip already wraps; what clipped it is the root's `overflow-hidden` above, which is
          there for the `rounded-lg` corners and has to stay. `shrink-0` stops the taller
          wrapped strip being compressed by the `flex flex-col` it sits in, so the rows it needs
          are the rows it gets and the editor body gives up the height instead.
          No toolbar while locked. */}
      {!locked && (
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-border bg-muted/30 max-md:shrink-0">
        {fontControls && (
          <>
            <select
              aria-label="Font"
              title="Font"
              value={fontValue}
              onChange={(e) => {
                const chain = editor.chain().focus();
                (e.target.value ? chain.setFontFamily(e.target.value) : chain.unsetFontFamily()).run();
              }}
              className={cn(TOOLBAR_SELECT, "w-[130px]")}
            >
              <option value="">Default font</option>
              {FONT_FAMILIES.map((font) => (
                <option key={font.label} value={font.css} style={{ fontFamily: font.css }}>
                  {font.label}
                </option>
              ))}
              {fontValue && !FONT_FAMILIES.some((font) => font.css === fontValue) && (
                <option value={fontValue}>{fontValue.split(",")[0].replace(/["']/g, "")}</option>
              )}
            </select>
            <select
              aria-label="Font size"
              title="Font size"
              value={sizeValue}
              onChange={(e) => {
                const chain = editor.chain().focus();
                (e.target.value ? chain.setFontSize(e.target.value) : chain.unsetFontSize()).run();
              }}
              className={cn(TOOLBAR_SELECT, "ml-1 w-20")}
            >
              <option value="">Size</option>
              {FONT_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
              {sizeValue && !FONT_SIZES.includes(sizeValue) && <option value={sizeValue}>{sizeValue}</option>}
            </select>

            <div className="w-px h-5 bg-border mx-1" />
          </>
        )}
        <ToolbarButton
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold"
        >
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic"
        >
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Underline"
        >
          <UnderlineIcon className="h-4 w-4" />
        </ToolbarButton>
        {fontControls && (
          <label
            title="Text Color"
            className="h-8 inline-flex items-center gap-1 rounded-md px-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <Type className="h-4 w-4" />
            {/* No focus() per change: moving focus while the picker is open would close it. */}
            <input
              type="color"
              aria-label="Text color"
              value={toHex(textStyle.color)}
              onChange={(e) => editor.chain().setColor(e.target.value).run()}
              className="h-6 w-8 cursor-pointer rounded border border-border bg-transparent p-0"
            />
          </label>
        )}

        <div className="w-px h-5 bg-border mx-1" />

        <ToolbarButton
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          title="Heading"
        >
          <Heading2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet List"
        >
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Numbered List"
        >
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>

        <div className="w-px h-5 bg-border mx-1" />

        <ToolbarButton
          active={editor.isActive({ textAlign: "left" })}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          title="Align Left"
        >
          <AlignLeft className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive({ textAlign: "center" })}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          title="Align Center"
        >
          <AlignCenter className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive({ textAlign: "right" })}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          title="Align Right"
        >
          <AlignRight className="h-4 w-4" />
        </ToolbarButton>

        <div className="w-px h-5 bg-border mx-1" />

        {/* Table controls */}
        <ToolbarButton
          onClick={() =>
            editor
              .chain()
              .focus()
              .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
              .run()
          }
          title="Insert Table"
        >
          <TableIcon className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().addRowAfter().run()}
          title="Add Row"
        >
          <Rows className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().addColumnAfter().run()}
          title="Add Column"
        >
          <Columns className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().deleteRow().run()}
          title="Delete Row"
        >
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium">
            <Rows className="h-3.5 w-3.5" />
            <Trash2 className="h-3 w-3" />
          </span>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().deleteColumn().run()}
          title="Delete Column"
        >
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium">
            <Columns className="h-3.5 w-3.5" />
            <Trash2 className="h-3 w-3" />
          </span>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().deleteTable().run()}
          title="Delete Table"
        >
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium">
            <TableIcon className="h-3.5 w-3.5" />
            <Trash2 className="h-3 w-3" />
          </span>
        </ToolbarButton>

        <div className="w-px h-5 bg-border mx-1" />

        <ToolbarButton
          active={editor.isActive("link")}
          onClick={addLink}
          title="Link"
        >
          <LinkIcon className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton onClick={addImage} title="Image">
          <Image className="h-4 w-4" />
        </ToolbarButton>

        <div className="flex-1" />

        {showSourceToggle && (
          <ToolbarButton
            active={sourceMode}
            onClick={toggleSourceMode}
            title={sourceMode ? "Switch to rich editor" : "Edit HTML source"}
          >
            <Code2 className="h-4 w-4" />
          </ToolbarButton>
        )}

        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          title="Undo"
        >
          <Undo className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          title="Redo"
        >
          <Redo className="h-4 w-4" />
        </ToolbarButton>
      </div>
      )}

      {sourceMode ? (
        <textarea
          value={sourceValue}
          onChange={(e) => setSourceValue(e.target.value)}
          spellCheck={false}
          className={cn(
            "block w-full flex-1 min-h-[260px] resize-y px-4 py-3 font-mono text-xs leading-relaxed bg-background outline-none overflow-y-auto",
            editorClassName,
          )}
        />
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <EditorContent editor={editor} />
        </div>
      )}
    </div>
  );
}
